/**
 * Máquina de estados do pedido unificado da Plataforma (Task 12, brief 3.3 /
 * D-12.2). **Leia isto antes de mexer — é o coração da task.**
 *
 * Endereço CANÔNICO (Task 13, limpeza de arquitetura): esta é a regra pura
 * promovida de `apps/plataforma/src/orders/state-machine.ts` para o núcleo
 * compartilhado `packages/crm`, conforme o handoff H-12.A e o ADR-0004. A
 * Edge Function `supabase/functions/crm-runtime` e o app `apps/plataforma`
 * IMPORTAM daqui (a dependência deixou de apontar app→borda→app). O arquivo
 * antigo em `apps/plataforma/src/orders/state-machine.ts` passou a reexportar
 * deste módulo para não quebrar os consumidores existentes.
 *
 * Invariantes deste módulo:
 *
 * 1. **Puro e sem I/O.** Nada aqui faz fetch, lê env, toca DOM ou banco. É por
 *    isso que a MESMA função valida a transição no servidor (`apps/api-dev`
 *    hoje; Edge Function depois) e decide o que a tela oferece. Duas
 *    implementações da máquina de estados dariam dois pedidos diferentes para
 *    o mesmo registro.
 * 2. **O vocabulário é o da persistência.** `ORDER_STATES`, `ORDER_ORIGINS` e
 *    os campos exigidos por transição são exatamente os `check (... in (...))`
 *    de `platform_orders`/`platform_order_events` (migration 0031), e um teste
 *    compara os dois lados. Se o SQL e o código divergirem, o teste falha.
 * 3. **Transição sem o dado que a justifica é recusada.** Faturar sem
 *    referência de NF, despachar sem transportadora/rastreio, cancelar/devolver
 *    sem motivo e pagar sem referência de pagamento não são transições
 *    "incompletas": são transições INVÁLIDAS. A recusa é explícita (a tela e o
 *    chamador precisam saber o que falta), nunca um estado gravado pela metade.
 * 4. **Nada de emissão fiscal aqui** (D-12.3): o faturamento registra a
 *    REFERÊNCIA da NF que veio do Bling (número/chave). A Plataforma não emite
 *    documento fiscal.
 */
/** Canal de ORIGEM do pedido. Preservar a origem é requisito do brief 3.3. */
export const ORDER_ORIGINS = ["loja", "whatsapp", "marketplace", "manual"];
/** Rótulo exibido ao operador. WhatsApp aparece como "WAPI da TDZAIN" (D-00). */
export const ORDER_ORIGIN_LABELS = {
    loja: "Loja (checkout do site)",
    whatsapp: "WhatsApp (WAPI da TDZAIN)",
    marketplace: "Marketplace",
    manual: "Venda registrada manualmente"
};
/**
 * Estados do pedido unificado (D-12.2). A linha principal é
 * `payment_pending → paid → separation → shipping → invoiced → delivered`;
 * `cancelled` e `returned` são os dois ramos de saída.
 */
export const ORDER_STATES = [
    "payment_pending",
    "paid",
    "separation",
    "shipping",
    "invoiced",
    "delivered",
    "cancelled",
    "returned"
];
export const ORDER_STATE_LABELS = {
    payment_pending: "Aguardando pagamento",
    paid: "Pago",
    separation: "Em separação",
    shipping: "Em expedição/transporte",
    invoiced: "Faturado (NF do Bling referenciada)",
    delivered: "Entregue",
    cancelled: "Cancelado",
    returned: "Devolvido"
};
/**
 * Transições permitidas.
 *
 * Duas escolhas que merecem explicação (D-12.6):
 * - **`cancelled` só antes do despacho.** Depois que o pedido saiu
 *   (`shipping`/`invoiced`/`delivered`), o caminho de volta é DEVOLUÇÃO
 *   (`returned`), que tem logística reversa e tratamento fiscal próprios.
 *   Oferecer "cancelar" com a mercadoria na rua esconderia a reversa.
 * - **`invoiced` depois de `shipping`.** É a sequência decidida em D-12.2. A
 *   Plataforma não emite NF: ela registra a referência quando o Bling fatura,
 *   e na operação atual isso acontece junto/depois da expedição.
 * - **`delivered` só sai de `invoiced`** (D-12.14). O faturamento não é etapa
 *   opcional: `shipping → delivered` deixaria o pedido terminar a venda sem
 *   nenhum evento fiscal nem `nfReference`, o que é histórico incompleto para
 *   uma venda concluída. Quem precisa registrar a entrega fatura primeiro
 *   (referenciando a NF do Bling); a recusa é explícita (422), então a tela e
 *   o chamador aprendem a ordem em vez de gravar uma entrega sem documento.
 */
export const ALLOWED_TRANSITIONS = {
    payment_pending: ["paid", "cancelled"],
    paid: ["separation", "cancelled"],
    separation: ["shipping", "cancelled"],
    shipping: ["invoiced", "returned"],
    invoiced: ["delivered", "returned"],
    delivered: ["returned"],
    cancelled: [],
    returned: []
};
/**
 * Estados em que a venda **aconteceu e vale** — o recorte de "histórico de
 * compras" do cliente (Task 13) e a base do que o financeiro gerencial vai
 * materializar (Task 16).
 *
 * `payment_pending` fica fora porque a venda ainda não se confirmou;
 * `cancelled` e `returned` ficam fora porque a venda foi desfeita. Somar
 * pedido devolvido no total gasto de um cliente diria que ele comprou algo que
 * voltou para o estoque — e o 360 passaria a mentir sobre o relacionamento.
 */
export const ORDER_REVENUE_STATES = [
    "paid",
    "separation",
    "shipping",
    "invoiced",
    "delivered"
];
export function isRevenueState(state) {
    return ORDER_REVENUE_STATES.includes(state);
}
/** Estados finais: nada sai deles (a lista de transições é vazia). */
export const TERMINAL_STATES = ORDER_STATES.filter((state) => ALLOWED_TRANSITIONS[state].length === 0);
export function isOrderState(value) {
    return typeof value === "string" && ORDER_STATES.includes(value);
}
export function isOrderOrigin(value) {
    return typeof value === "string" && ORDER_ORIGINS.includes(value);
}
export function isTerminalState(state) {
    return ALLOWED_TRANSITIONS[state].length === 0;
}
export function nextStates(state) {
    return ALLOWED_TRANSITIONS[state];
}
export function canTransition(from, to) {
    return ALLOWED_TRANSITIONS[from].includes(to);
}
/** Campos obrigatórios por estado de destino. Estado ausente = nada exigido. */
export const TRANSITION_REQUIREMENTS = {
    paid: ["paymentReference"],
    shipping: ["carrier", "trackingCode"],
    invoiced: ["nfReference"],
    cancelled: ["reason"],
    returned: ["reason"]
};
/** Rótulo do campo exigido, usado pelo formulário e pela mensagem de recusa. */
export const TRANSITION_FIELD_LABELS = {
    paymentReference: "Referência do pagamento (id no provedor)",
    carrier: "Transportadora",
    trackingCode: "Código de rastreio",
    nfReference: "Número/chave da NF emitida no Bling",
    reason: "Motivo"
};
export function requirementsFor(to) {
    return TRANSITION_REQUIREMENTS[to] ?? [];
}
/** Campos exigidos pela transição que não vieram preenchidos. */
export function missingTransitionRequirements(to, input = {}) {
    return requirementsFor(to).filter((field) => {
        const value = input[field];
        return typeof value !== "string" || value.trim() === "";
    });
}
/**
 * Única porta de validação de transição — usada pelo servidor (antes de
 * gravar) e pela tela (antes de oferecer). Devolve o motivo da recusa em vez
 * de lançar: quem chama precisa dizer ao operador O QUE falta.
 */
export function checkTransition(from, to, input = {}) {
    if (!isOrderState(to))
        return { ok: false, error: "unknown_state", fields: ["to"] };
    if (!canTransition(from, to))
        return { ok: false, error: "invalid_transition", fields: [from, to] };
    const missing = missingTransitionRequirements(to, input);
    if (missing.length)
        return { ok: false, error: "transition_requirements_missing", fields: missing };
    return { ok: true };
}
/**
 * Estado do pedido unificado derivado do pedido do checkout da Loja.
 *
 * Por que existe: o pedido da Loja NÃO é recadastrado na Plataforma (D-12.1) —
 * ele é projetado, preservando `origin: "loja"` e o id do registro de origem.
 * Enquanto a operação não registra nenhuma transição, o estado do pedido
 * unificado é o do checkout, traduzido para o vocabulário da máquina; por isso
 * a tradução é pura e testável, e vem com a nota que explica a tradução.
 *
 * `payment_failed` e `awaiting_shipping_quote` não têm estado próprio na
 * máquina: os dois continuam "aguardando pagamento" (o pedido não avançou),
 * mas a nota diz qual é o bloqueio real em vez de apagá-lo.
 */
export function stateFromCheckoutStatus(status) {
    switch (status) {
        case "paid":
            return { state: "paid", note: "Pagamento aprovado no checkout da Loja." };
        case "payment_failed":
            return {
                state: "payment_pending",
                note: "Pagamento recusado no checkout da Loja: o pedido segue aguardando pagamento."
            };
        case "awaiting_shipping_quote":
            return {
                state: "payment_pending",
                note: "Aguardando cotação de frete: sem total fechado o pedido não pode ser pago."
            };
        case "awaiting_payment":
            return { state: "payment_pending", note: "Pedido criado no checkout da Loja, aguardando pagamento." };
        default:
            // Status desconhecido não vira "pago" nem desaparece: fica no início da
            // máquina com o valor original declarado, para a tela mostrar o que veio.
            return { state: "payment_pending", note: `Status do checkout não mapeado: ${status}.` };
    }
}
